import {useState} from "react";
import AddBoxOutlinedIcon from "@mui/icons-material/AddBoxOutlined";
import TuneIcon from "@mui/icons-material/Tune";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import CalculateIcon from "@mui/icons-material/Calculate";
import VerticalAlignTopIcon from "@mui/icons-material/VerticalAlignTop";
import VerticalAlignBottomIcon from "@mui/icons-material/VerticalAlignBottom";
import {ElevationApp, workspaceSelection} from "../ElevationApp";
import {MdFrameJoint} from "../model/frame/MdFrameBlock";
import {frameProfileLabels, MdFrameProfileType} from "../model/frame/MdFrameSpec";
import {useEventEmitter2} from "../util/useEventEmitter2";
import {useFrmVar} from "../util/useFrmVar";
import {RibbonButton, RibbonGroup, RibbonSelect, RibbonStepper} from "./Ribbon";
import {JOINT_OPTIONS} from "./JointOptions";
import {supportedProfiles} from "../scenes/frameProfile/Helpers";
import {CutDimensionDialog} from "./CutDimensionDialog";
import {StdHeightDialog} from "./StdHeightDialog";
import {getHingeLines} from "../../hardware/hingeProducts";
import {MenubarShortcut, useMenubarShortcuts} from "./useMenubarShortcuts";
import {Workspace} from "../model/view/MdViewSettings";
import {useConfirm} from "../../../../../misc/Confirm";

// the Reset button's label and confirm text for each workspace.
const RESET_TEXT: Record<Workspace, {label: string; what: string}> = {
    screen: {label: "Reset Frame", what: "the frame elevation"},
    door: {label: "Reset Door", what: "the door elevation"},
    hardware: {label: "Reset Hardware", what: "the hardware elevation"},
};

// the profiles the picker offers, labelled. Limited to supportedProfiles (those the
// frame cross-section scenes can actually render).
const PROFILE_OPTIONS = supportedProfiles.map(p => ({value: p, label: frameProfileLabels[p]}));

// the editing groups of a workspace's ribbon tab (see WorkspaceTab): frame-wide
// settings. The Frame group sets the default joint applied to every corner with no
// explicit per-joint override (per-corner and per-mullion joints are edited from the
// Selected Stick tab). The Door group offers "Add Lite" on the door-elevation views.
export function EditGroups(props: {app: ElevationApp}) {
    const app = props.app;

    // re-render so the active state tracks the current default.
    useEventEmitter2(app.frameSpec.frameBlocksChanged);
    const jointDefault = useEventEmitter2(app.frameSpec.frameBlockJointDefault);
    const activeView = useEventEmitter2(app.viewSettings.activeView);
    const workspace = useEventEmitter2(app.viewSettings.workspace) ?? "screen";

    // which groups the workspace's tab carries. Each group below is gated on both its
    // view and one of these: "screen" keeps the frame profile/joint and the frame
    // cutdown, "door" keeps the door-lite tools and the door over/under cuts,
    // "hardware" keeps the hardware/hinge tools.
    const wsProfile = workspace === "screen";
    const wsFrameJoint = workspace === "screen";
    const wsDoorLite = workspace === "door";
    const wsHardware = workspace === "hardware";
    const wsHinges = workspace === "hardware";
    const wsDoorCuts = workspace === "door";
    const wsFrameCuts = workspace === "screen";

    const setDefault = (type: MdFrameJoint) => {
        app.frameSpec.frameBlockJointDefault.emit(type);
        // serialized in FrameDesign but emits none of the tracked change signals,
        // so record the undo step explicitly.
        app.history.captureNow();
    };

    const confirm = useConfirm();
    // a global door edit saves only the door, so only the door resets.
    const resetWorkspace: Workspace = app.editGlobal ? "door" : workspace;
    const resetText = RESET_TEXT[resetWorkspace];

    const resetElevation = async () => {
        try {
            await confirm.confirm(`Reset ${resetText.what} to the default? This removes all changes to ${resetText.what}.`);
        } catch {
            return;
        }

        app.resetElevations(workspaceSelection(resetWorkspace));
    };

    // which cut dialog is open (null when none). The dialog collects the dimension
    // for a new door overcut / door undercut / frame undercut.
    const [cutDialog, setCutDialog] = useState<null | "overcut" | "undercut" | "frameUndercut">(null);

    // whether the "STD Height" dialog is open: it collects a nominal height, then
    // applies the standard hinge spacing + count for it across all hinges.
    const [stdHeightOpen, setStdHeightOpen] = useState(false);

    // re-render when the hinge list changes (count/spacing edits, reset-to-auto).
    useEventEmitter2(app.hardware.hinges.listChanged);
    const hingeCount = app.hardware.hinges.list.length;

    // a cut is "active" once its dimension is non-zero; the button then flips to a
    // remove action. Bound so the label tracks add/remove.
    const overcut = useFrmVar(app.doorSpec.overcut);
    const undercut = useFrmVar(app.doorSpec.undercut);
    const frameUndercut = useFrmVar(app.frameSpec.undercutFrame);

    const doorView = activeView === "door-active" || activeView === "door-inactive";
    const locationView = activeView === "location-active" || activeView === "location-inactive";

    // the group's default hinge product (both leaves): what a hinge uses when it has
    // no per-location override. Only offered when there's more than one hinge product
    // to pick between. Setting it resets every hinge to the chosen product.
    const hingeProducts = getHingeLines(app.hardwareGroup?.hardware ?? []);
    const defaultHingeProduct = app.hardware.hinges.getDefaultProduct(app.hardwareGroup?.hardware ?? []);
    // the frame overviews (frame-grid / all) both render the frame block grid, so
    // they share the Frame joint actions and the Add Lite action. Add Lite defaults
    // to the active leaf there; the inactive-door view is the only place that
    // targets the inactive leaf.
    const frameView = activeView === "frame-grid" || activeView === "all";
    const canAddLite = doorView || frameView;

    // the frame cross-section views (profile / top-down / all) draw the jamb profile,
    // so the profile picker shows there.
    const profileView = activeView === "frame-profile-active" || activeView === "frame-profile-inactive" || activeView === "top-down" || activeView === "all";

    // switch the frame profile: reset the profile-dependent cut properties (shared with
    // fromOpening) and rebuild the active scene so the new cross-section draws.
    const changeProfile = (profile: MdFrameProfileType) => {
        app.frameSpec.setProfile(profile);
        // setProfile mutates silently (setInitial — no anyUserSet, no structural emit),
        // so record the undo step explicitly.
        app.history.captureNow();
        if(activeView) app.showView(activeView);
    };

    const addLite = (leaf: "active" | "inactive") => {
        const leafWidth = leaf === "active" ? app.doorSpec.activeDoorWidth.get() : app.doorSpec.inActiveDoorWidth.get();
        const leafHeight = app.doorSpec.actualHeight.get();
        const lite = app.doorSpec.addLite(leaf, leafWidth, leafHeight);
        // select the new lite so its Selected tab (with Remove) is ready.
        app.viewSettings.selectItem({type: "door-lite", id: lite.id, leaf: lite.leaf}, false);
        app.viewSettings.activeTab.emit("selected");
    };

    // the leaf a plain Add Lite click targets: the inactive-door view targets its
    // own leaf, everything else the active leaf. A pair additionally gets a
    // dropdown to pick the other leaf explicitly.
    // the Hardware group is available anywhere a door leaf or the frame grid is on
    // screen, and on the hardware workspace's Locations view (its focus).
    const canManageHardware = doorView || frameView || (workspace === "hardware" && locationView);

    const defaultLeaf = activeView === "door-inactive" ? "inactive" : "active";
    const liteDropdown = app.frameSpec.isPair
        ? [
            {label: "Add to Active", onClick: () => addLite("active")},
            {label: "Add to Inactive", onClick: () => addLite("inactive")},
        ]
        : undefined;

    // L for Lite. Free alongside the cell shortcuts on the frame grid (G/F fill, V/H split),
    // so it works on the door views and the frame overviews alike. Only registered where the
    // Add Lite button itself shows, so it can't fire from a view that has no leaf on screen.
    const addLiteShortcut: MenubarShortcut = {
        keys: ["l"], display: "L",
        hint: "Press L to add a lite",
        run: () => addLite(defaultLeaf),
    };

    useMenubarShortcuts(canAddLite && wsDoorLite ? [addLiteShortcut] : []);

    return (
        <>
            {profileView && wsProfile && (
                <RibbonGroup label="Default Profile">
                    <RibbonSelect
                        value={app.frameSpec.profile}
                        options={PROFILE_OPTIONS}
                        onChange={changeProfile}
                    />
                </RibbonGroup>
            )}

            {frameView && wsFrameJoint && (
                <RibbonGroup label="Frame">
                    {JOINT_OPTIONS.map(o => (
                        <RibbonButton
                            key={o.type}
                            icon={o.icon}
                            label={o.label}
                            active={jointDefault === o.type}
                            onClick={() => setDefault(o.type)}
                        />
                    ))}
                </RibbonGroup>
            )}

            {canAddLite && wsDoorLite && (
                <RibbonGroup label="Door">
                    <RibbonButton
                        icon={<AddBoxOutlinedIcon />}
                        label="Add Lite"
                        shortcut={addLiteShortcut.display}
                        onClick={addLiteShortcut.run}
                        dropdown={liteDropdown}
                    />
                </RibbonGroup>
            )}

            {canManageHardware && wsHardware && (
                <RibbonGroup label="Hardware">
                    <RibbonButton
                        icon={<TuneIcon />}
                        label="Manage"
                        onClick={() => app.viewSettings.hardwareSidebarCollapsed.emit(false)}
                    />
                </RibbonGroup>
            )}

            {(doorView || locationView) && wsHinges && (
                <RibbonGroup label="Hinges">
                    <RibbonStepper
                        value={hingeCount}
                        min={1}
                        onChange={count => app.hardware.hinges.setCount(count)}
                    />
                    <RibbonButton
                        icon={<RestartAltIcon />}
                        label="Auto"
                        onClick={() => app.hardware.hinges.resetToAuto()}
                    />
                    {locationView && (
                        <RibbonButton
                            icon={<CalculateIcon />}
                            label="STD Height"
                            onClick={() => setStdHeightOpen(true)}
                        />
                    )}
                </RibbonGroup>
            )}

            {locationView && hingeProducts.length > 1 && wsHinges && (
                <RibbonGroup label="Default Hinge">
                    <RibbonSelect
                        value={defaultHingeProduct?.toString() ?? ""}
                        options={hingeProducts.map(h => ({value: h.product.toString(), label: h.productCode}))}
                        onChange={v => app.hardware.hinges.setDefaultProduct(parseInt(v))}
                    />
                </RibbonGroup>
            )}

            {(doorView || locationView) && wsDoorCuts && (
                <RibbonGroup label="Door Cuts">
                    <RibbonButton
                        icon={<VerticalAlignTopIcon />}
                        label={overcut > 0 ? "Remove Overcut" : "Add Overcut"}
                        active={overcut > 0}
                        onClick={() => {
                            if(overcut > 0) app.doorSpec.overcut.userSet(0, {debug: "editTab.removeOvercut"});
                            else setCutDialog("overcut");
                        }}
                    />
                    <RibbonButton
                        icon={<VerticalAlignBottomIcon />}
                        label={undercut > 0 ? "Remove Undercut" : "Add Undercut"}
                        active={undercut > 0}
                        onClick={() => {
                            if(undercut > 0) app.doorSpec.undercut.userSet(0, {debug: "editTab.removeUndercut"});
                            else setCutDialog("undercut");
                        }}
                    />
                </RibbonGroup>
            )}

            {(frameView || locationView) && wsFrameCuts && (
                <RibbonGroup label="Frame Cuts">
                    <RibbonButton
                        icon={<VerticalAlignBottomIcon />}
                        label={frameUndercut > 0 ? "Remove Undercut" : "Add Undercut"}
                        active={frameUndercut > 0}
                        onClick={() => {
                            if(frameUndercut > 0) app.frameSpec.undercutFrame.userSet(0, {debug: "editTab.removeFrameUndercut"});
                            else setCutDialog("frameUndercut");
                        }}
                    />
                </RibbonGroup>
            )}

            <RibbonGroup label="Elevation">
                <RibbonButton
                    icon={<RestartAltIcon />}
                    label={resetText.label}
                    onClick={resetElevation}
                />
            </RibbonGroup>

            <CutDimensionDialog
                open={cutDialog !== null}
                title={cutDialog === "overcut" ? "Add Overcut" : "Add Undercut"}
                sys={app.project?.unitSystem || "imperial"}
                onClose={() => setCutDialog(null)}
                onSubmit={value => {
                    if(cutDialog === "overcut") app.doorSpec.overcut.userSet(value, {debug: "editTab.addOvercut"});
                    else if(cutDialog === "undercut") app.doorSpec.undercut.userSet(value, {debug: "editTab.addUndercut"});
                    else if(cutDialog === "frameUndercut") app.frameSpec.undercutFrame.userSet(value, {debug: "editTab.addFrameUndercut"});
                    setCutDialog(null);
                }}
            />

            <StdHeightDialog
                open={stdHeightOpen}
                sys={app.project?.unitSystem || "imperial"}
                onClose={() => setStdHeightOpen(false)}
                onSubmit={height => {
                    app.hardware.hinges.applyStdHeight(height);
                    setStdHeightOpen(false);
                }}
            />

        </>
    );
}
